import z from 'zod'

/**
 * __CustomerId__
 *
 * The unique identifier for a customer.
 */
export const CustomerIdSchema = z.string().brand('CustomerId')
export type CustomerId = z.infer<typeof CustomerIdSchema>

/**
 * __RemoteCustomerId__
 *
 * The identifier for a customer in an external third-party system.
 */
export const RemoteCustomerIdSchema = z.string().brand('RemoteCustomerId')
export type RemoteCustomerId = z.infer<typeof RemoteCustomerIdSchema>

/**
 * __MergeContactId__
 *
 * The unique identifier for a contact entity in a merge.dev integration.
 */
export const MergeContactIdSchema = z.string().brand('MergeContactId')
export type MergeContactId = z.infer<typeof MergeContactIdSchema>

//TODO: Not sure we need these transforms - but atm a bunch of tests fail with null values, and it touches so many things that I don't have time to check them all right now.
// A customer is an external user who uses a subscriber's product.
export const CustomerSchema = z.object({
  // The unique ID of the customer.
  id: CustomerIdSchema,
  // The ID of the subscriber to whom the customer belongs.
  subscriberId: z.string().brand('SubscriberId'),
  // The ID of the Contact entity in a merge.dev integration, if this customer
  // is present in a third-party system.
  mergeContactId: MergeContactIdSchema.nullish().transform(
    (val) => val ?? undefined,
  ),
  // The ID of the customer entity in an external third-party system, if this
  // customer is present there.
  remoteId: RemoteCustomerIdSchema.nullish()
    .transform((val) => val ?? undefined)
    .describe(
      'The unique identifier for the customer in an external third-party system.',
    ),
  subscriberCustomerId: z
    .string()
    .nullish()
    .transform((val) => val ?? undefined),
  displayName: z
    .string()
    .nullish()
    .transform((val) => val ?? undefined)
    .describe('The name of the customer as it should be displayed in the UI.'),
  firstName: z
    .string()
    .nullish()
    .transform((val) => val ?? undefined),
  lastName: z
    .string()
    .nullish()
    .transform((val) => val ?? undefined),
  email: z
    .string()
    .nullish()
    .transform((val) => val ?? undefined)
    .describe('The email address of the customer.'),
  // TEMPORARY while Step fixes the issue with the token disappearing from Intercom.
  // Step requires a short token to be able to authenticate a customer.
  subscriberToken: z
    .string()
    .nullish()
    .transform((val) => val ?? undefined),

  /**
   * __phoneNumber__
   *
   * The phone number of the customer.
   */
  phoneNumber: z.string().nullable().optional(),

  /**
   * __avatarUrl__
   *
   * The URL of the customer's avatar (if available).
   */
  avatarUrl: z.string().url().nullish(),
})

export type Customer = z.infer<typeof CustomerSchema>
export type CustomerForQA = Pick<
  Customer,
  'displayName' | 'email' | 'phoneNumber' | 'id'
>
